import { useState } from 'react';
import {
  FormProvider,
  useFieldArray,
  useFormContext,
  useWatch,
  type UseFormReturn,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  buildLogisticsCalculationSheetModel,
  type LogisticsCalculationPublicDto,
} from '@magimp/shared';
import { useToastStore } from '../../../stores/toast.js';
import { Button, useNarrow } from '../../../ui/index.js';
import {
  LogisticsCalculationFormSchema,
  calculateEditable,
  candidateToCargo,
  heldCargoOf,
  mskDate,
  type LogisticsCalculationFormValues,
} from '../form-model.js';
import { DocumentSheet } from '../sheet/DocumentSheet.js';
import { CargoPicker } from './CargoPicker.js';
import { DocumentToolbar } from './DocumentToolbar.js';

type Form = LogisticsCalculationFormValues;
type SavedDraft = { id: string; publicCode: string; documentDate: string };

export function LogisticsCalculationForm({
  form,
  savedDraft,
  notice,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
  onBack,
}: {
  form: UseFormReturn<Form>;
  savedDraft?: SavedDraft;
  /** What the surface has to say about the document before it is saved. */
  notice?: string | undefined;
  busy: boolean;
  submitLabel: string;
  onSubmit: (content: Form) => void | Promise<unknown>;
  onCancel: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const showToast = useToastStore((state) => state.show);
  const [invalidSubmit, setInvalidSubmit] = useState(false);
  const cargos = useFieldArray({ control: form.control, name: 'cargos' });
  /**
   * A save the server refused because another document holds one of the cargos.
   *
   * WHY here and not at the two mutation call sites: the row to mark and the
   * form to leave editable are this component's, and both callers refuse the
   * same way. Every other failure stays with the caller that knows what it was
   * trying to do.
   */
  const reportHeldCargo = (error: unknown) => {
    const held = heldCargoOf(error);
    if (!held) return;
    const index = form
      .getValues('cargos')
      .findIndex((cargo) => cargo.sourceCargoArticleId === held.cargoArticleId);
    if (index >= 0) form.setError(`cargos.${index}.article`, { type: 'held' });
    showToast({
      tone: 'error',
      text: held.publicCode
        ? t('editor.cargoHeld', { code: held.publicCode })
        : t('editor.cargoHeldUnknown'),
    });
  };
  // WHY the one form stays mounted below the editing width: filling the sheet
  // in is a desk task (#2883 FR-5), but a manager who narrows the window
  // mid-edit must get the document back to read — not lose what they typed.
  const narrow = useNarrow();
  return (
    <FormProvider {...form}>
      <form
        className="doc-page"
        data-testid="rl-document-editor"
        onSubmit={form.handleSubmit(
          async (content) => {
            setInvalidSubmit(false);
            try {
              await onSubmit(content);
            } catch (error) {
              reportHeldCargo(error);
            }
          },
          () => setInvalidSubmit(true),
        )}
      >
        <DocumentToolbar onBack={onBack}>
          <Button kind="ghost" size="sm" disabled={busy} onClick={onCancel}>
            {t('actions.cancel')}
          </Button>
          <Button kind="primary" size="sm" type="submit" disabled={busy}>
            {submitLabel}
          </Button>
        </DocumentToolbar>
        {invalidSubmit ? (
          <p className="doc-page-alert" role="alert">
            {t('editor.validationError')}
          </p>
        ) : null}
        {notice ? (
          <p className="doc-page-alert" role="status" data-testid="rl-document-notice">
            {notice}
          </p>
        ) : null}
        {narrow ? (
          <p className="doc-narrow-notice" data-testid="rl-document-narrow-notice">
            {t('document.narrowNotice')}
          </p>
        ) : null}
        <EditableDocument
          savedDraft={savedDraft}
          narrow={narrow}
          cargoKeys={cargos.fields.map((field) => field.id)}
          onRemoveCargo={(index) => cargos.remove(index)}
          onAddCargo={(cargo) => cargos.append(cargo)}
          onToast={(text, tone) => showToast({ tone, text })}
        />
      </form>
    </FormProvider>
  );
}

function EditableDocument({
  savedDraft,
  narrow,
  cargoKeys,
  onRemoveCargo,
  onAddCargo,
  onToast,
}: {
  savedDraft: SavedDraft | undefined;
  narrow: boolean;
  cargoKeys: readonly string[];
  onRemoveCargo: (index: number) => void;
  onAddCargo: (cargo: Form['cargos'][number]) => void;
  onToast: (text: string, tone: 'success' | 'error') => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const publicCode = savedDraft?.publicCode ?? t('editor.numberAfterSave');
  const [fallbackDate] = useState(mskDate);
  const documentDate = savedDraft?.documentDate ?? fallbackDate;
  const dto = usePreviewDocument(publicCode, documentDate);
  // INVARIANT: below the editing width the sheet is handed the same view mode
  // the customer's page gets — no `editing`, and none of the affordances that
  // only make sense while filling it in. Hiding controls with CSS would leave
  // the form elements in the tree and print them.
  const editable = narrow
    ? {}
    : {
        editing: true,
        onRemoveCargo,
        cargoPicker: (
          <DocumentCargoPicker
            openCalculationId={savedDraft?.id}
            onAddCargo={onAddCargo}
            onToast={onToast}
          />
        ),
      };
  return <DocumentSheet dto={dto} publicCode={publicCode} cargoKeys={cargoKeys} {...editable} />;
}

/**
 * The money the document shows while it is being filled in.
 *
 * WHY the schema gate: `calculateLogisticsCalculation` divides by the document
 * rates, so an incomplete draft has no total to show. Every money cell then
 * prints a dash while the text cells keep taking input from the form.
 */
function usePreviewDocument(
  publicCode: string,
  documentDate: string,
): LogisticsCalculationPublicDto | null {
  const { control } = useFormContext<Form>();
  const values = useWatch({ control });
  const parsed = LogisticsCalculationFormSchema.safeParse(values);
  if (!parsed.success) return null;
  const { content, calculation } = calculateEditable(parsed.data, documentDate);
  return buildLogisticsCalculationSheetModel(content, calculation, {
    publicCode,
  });
}

function DocumentCargoPicker({
  openCalculationId,
  onAddCargo,
  onToast,
}: {
  openCalculationId: string | undefined;
  onAddCargo: (cargo: Form['cargos'][number]) => void;
  onToast: (text: string, tone: 'success' | 'error') => void;
}) {
  const { t } = useTranslation('logisticsCalculations');
  const { control, getValues } = useFormContext<Form>();
  const cargoValues = useWatch({ control, name: 'cargos' }) ?? [];
  const excluded = new Set(
    cargoValues.flatMap((cargo) =>
      cargo.sourceCargoArticleId ? [cargo.sourceCargoArticleId] : [],
    ),
  );
  return (
    <div className="doc-picker" data-editing="" data-testid="rl-document-cargo-picker">
      <CargoPicker
        excludedCandidateIds={excluded}
        openCalculationId={openCalculationId}
        onConfirm={(candidate) => {
          const duplicate = getValues('cargos').some(
            (cargo) => cargo.sourceCargoArticleId === candidate.id,
          );
          if (duplicate) {
            onToast(t('editor.cargoPicker.duplicate', { article: candidate.article }), 'error');
            return false;
          }
          onAddCargo(candidateToCargo(candidate));
          onToast(t('editor.cargoPicker.added', { article: candidate.article }), 'success');
          return true;
        }}
      />
    </div>
  );
}
